import { Platform } from 'react-native';
import { offlineDatabase, OfflineReview, OfflineResponse, RecoverableWorkItem } from './offlineDatabase';
import { cacheService } from './cacheService';
import { reviewCoreApi } from './reviewCoreApi';
import {
  HomeReview,
  ReviewContactDetails,
  ReviewResponse,
  ReviewItem,
  Room,
} from '../types';
import {
  OfflineOnDemandItemSyncService,
} from './offlineOnDemandItemSync';
import { OfflineOnDemandItemMutationService } from './offlineOnDemandItemMutationService';
import { OfflineTempIdStore } from './offlineTempIdStore';
import { DraftConfig, OfflineDraftManager } from './offlineDraftManager';
import { OfflineSharedPrefillService } from './offlineSharedPrefillService';
import {
  OfflineAddressCacheService,
  OfflineAddressSuggestion,
} from './offlineAddressCacheService';
import { OfflineReviewCreationService } from './offlineReviewCreationService';
import { OfflineReviewReadService } from './offlineReviewReadService';
import { OfflineRoomSyncService } from './offlineRoomSyncService';
import { OfflineResponseMutationService } from './offlineResponseMutationService';
import { OfflineResponseSyncService } from './offlineResponseSyncService';
import { OfflineReviewMutationService } from './offlineReviewMutationService';
import { OfflineReviewRecordSyncService } from './offlineReviewRecordSyncService';
import {
  OfflineSyncDiscrepancy,
  OfflineSyncReconciliationService,
} from './offlineSyncReconciliationService';
import {
  OfflineSyncStats,
  OfflineSyncStatusService,
} from './offlineSyncStatusService';
import { CacheOnlineReviewOptions, OfflineReviewCacheService } from './offlineReviewCacheService';
import {
  createOfflineNetworkMonitor,
  OfflineNetworkMonitor,
} from './offlineNetworkMonitor';
import { OfflineFullSyncService } from './offlineFullSyncService';
import {
  getEffectiveNetworkOnline,
  isDetoxAutoSyncPaused,
} from '../utils/detoxTestControls';
import { OfflineSyncTriggerQueue } from './offlineSyncTriggerQueue';

type PendingReviewLifecycleAction = {
  type: 'submit' | 'complete';
  vendorNotes?: string;
  requestedAt: string;
};

type OfflineReviewMetadataWithLifecycle = Record<string, unknown> & {
  pendingLifecycleAction?: PendingReviewLifecycleAction;
};

export interface PhotoRecoveryCounts {
  photosUploaded: number;
  photosStillFailed: number;
  photosMissing: number;
}

export interface SelectedWorkRecoveryResult extends PhotoRecoveryCounts {
  recovered: number;
  skipped: number;
  failed: number;
}

class OfflineReviewService {
  private readonly PERIODIC_SYNC_INTERVAL_MS = 60 * 1000;
  private isOnline: boolean = false;
  private periodicSyncInterval: NodeJS.Timeout | null = null;
  private draftManager = new OfflineDraftManager((reviewId, itemId, data, options) =>
    this.updateOfflineResponse(
      reviewId,
      itemId,
      data,
      false,
      'pending',
      options?.mutationTimestamp
    )
  );
  private tempIdStore = new OfflineTempIdStore(offlineDatabase);
  private sharedPrefillService = new OfflineSharedPrefillService(offlineDatabase);
  private addressCacheService = new OfflineAddressCacheService(offlineDatabase);
  private reviewCreationService: OfflineReviewCreationService | null = null;
  private reviewMutationService: OfflineReviewMutationService | null = null;
  private reviewReadService: OfflineReviewReadService | null = null;
  private roomSyncService: OfflineRoomSyncService | null = null;
  private responseMutationService: OfflineResponseMutationService | null = null;
  private responseSyncService: OfflineResponseSyncService | null = null;
  private reviewRecordSyncService: OfflineReviewRecordSyncService | null = null;
  private onDemandItemMutationService: OfflineOnDemandItemMutationService | null = null;
  private syncReconciliationService: OfflineSyncReconciliationService | null = null;
  private syncStatusService: OfflineSyncStatusService | null = null;
  private reviewCacheService: OfflineReviewCacheService | null = null;
  private onDemandItemSyncService: OfflineOnDemandItemSyncService | null = null;
  private networkMonitor: OfflineNetworkMonitor;
  private fullSyncService: OfflineFullSyncService | null = null;
  private syncTriggerQueue: OfflineSyncTriggerQueue;
  private isInitialized: boolean = false;
  private initializationPromise: Promise<void> | null = null;
  private legacyRecoveryAdoptionCheckedReviewIds = new Set<number>();
  private failedReviewRecordSyncIds = new Set<number>();

  constructor() {
    this.syncTriggerQueue = new OfflineSyncTriggerQueue({
      isOnline: () => this.isOnline,
      isSyncing: () => this.getFullSyncService().isSyncing(),
      performFullSync: () => {
        if (isDetoxAutoSyncPaused()) {
          return Promise.resolve();
        }

        return this.performFullSync();
      },
      logger: console,
    });

    this.networkMonitor = createOfflineNetworkMonitor({
      onStatusChange: isOnline => {
        this.isOnline = isOnline;
        if (isOnline) {
          this.startPeriodicSync();
        } else {
          this.stopPeriodicSync();
        }
      },
      onBecameOnline: () => {
        if (isDetoxAutoSyncPaused()) {
          return Promise.resolve();
        }

        return this.performFullSync();
      },
      onBecameOnlineError: (source, error) => {
        const label = source === 'detox' ? 'Detox sync' : 'Sync';
        console.error(`[OfflineReviewService] ${label} after coming online failed:`, error);
      },
    });
  }

  /**
   * Initialize the offline review service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.initializeOnce();
    try {
      await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  }

  private async initializeOnce(): Promise<void> {

    try {
      // Initialize database
      await offlineDatabase.initialize();

      // A terminated process cannot clear the rows it had claimed. Recover
      // those markers before listeners or full-sync work can become active.
      await offlineDatabase.recoverInterruptedReviewSyncStatuses();

      // Repair and protect queued photo files before any startup sync can run.
      const { photoQueueService } = require('./photoQueueService');
      await photoQueueService.initialize();

      // Initialize cache service
      await cacheService.initialize();

      // Initialize network listener
      this.initializeNetworkListener();
      try {
        this.isOnline = await getEffectiveNetworkOnline();
      } catch {
        this.isOnline = false;
      }

      // Clean up old drafts
      this.draftManager.cleanupOldDrafts();

      // Load existing temp ID mappings
      await this.tempIdStore.loadMappings();

      // If online, run a startup safety sync before the periodic loop takes over.
      if (this.isOnline) {
        await this.performFullSync();
        this.startPeriodicSync();
      }

      this.isInitialized = true;

      // Defensive: If sync queue has items and we're online, trigger full sync
      // This catches items created before initialization completed
      if (this.syncTriggerQueue.hasPending() && this.isOnline) {
        this.syncTriggerQueue.scheduleDefensiveSync();
      }
    } catch (error) {
      console.error('[OfflineReviewService] ❌ FATAL ERROR during initialization:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw new Error(`Failed to initialize offline review service: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Configure draft auto-save settings
   */
  configureDrafts(config: Partial<DraftConfig>): void {
    this.draftManager.configure(config);
  }

  /**
   * Initialize network state listener
   */
  private initializeNetworkListener(): void {
    this.networkMonitor.start();
  }

  private startPeriodicSync(): void {
    if (Platform.OS === 'web' || this.periodicSyncInterval || isDetoxAutoSyncPaused()) {
      return;
    }

    this.periodicSyncInterval = setInterval(() => {
      if (!this.isOnline || isDetoxAutoSyncPaused()) {
        return;
      }

      this.performFullSync().catch(error => {
        console.error('[OfflineReviewService] Periodic sync failed:', error);
      });
    }, this.PERIODIC_SYNC_INTERVAL_MS);
  }

  private stopPeriodicSync(): void {
    if (!this.periodicSyncInterval) {
      return;
    }

    clearInterval(this.periodicSyncInterval);
    this.periodicSyncInterval = null;
  }

  private asLifecycleMetadata(value: unknown): OfflineReviewMetadataWithLifecycle {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? { ...(value as Record<string, unknown>) }
      : {};
  }

  // ====================
  // OFFLINE REVIEW CREATION
  // ====================

  /**
   * Create a new offline review with temporary ID
   */
  async createOfflineReview(
    propertyId: number,
    templateId: number,
    address?: string,
    details?: ReviewContactDetails
  ): Promise<HomeReview> {
    await this.initialize();
    return this.getReviewCreationService().createOfflineReview(
      propertyId,
      templateId,
      address,
      details
    );
  }

  /**
   * Get the most recent address for a property from cached reviews
   * Prefers completed reviews but falls back to in-progress reviews if none completed
   *
   * @param propertyId - The property ID to look up
   * @returns The most recent address for the property, or null if no reviews found
   */
  async getLatestAddressForProperty(propertyId: number): Promise<string | null> {
    return this.addressCacheService.getLatestAddressForProperty(propertyId);
  }

  /**
   * Get property ID for an address using normalized matching
   * Finds existing property ID to ensure review continuity across address variations
   *
   * @param address - The address to search for
   * @returns The property ID if found, null if this is a new property
   */
  async getPropertyIdForAddress(address: string): Promise<number | null> {
    await this.initialize();

    return this.addressCacheService.getPropertyIdForAddress(address);
  }

  /**
   * Get recent cached addresses for offline autocomplete
   * Returns both address and propertyId to maintain property consistency
   *
   * @param limit - Maximum number of addresses to return (default: 50)
   * @returns Array of {propertyId, address, lastUsed} from recent reviews
   */
  async getCachedAddresses(limit: number = 50): Promise<OfflineAddressSuggestion[]> {
    await this.initialize();

    return this.addressCacheService.getCachedAddresses(limit);
  }

  /**
   * Get offline review with full data
   */
  async getOfflineReview(reviewId: number | string): Promise<HomeReview> {
    await this.initialize();
    return this.getReviewReadService().getOfflineReview(reviewId);
  }

  async getOfflineReviews(): Promise<HomeReview[]> {
    await this.initialize();
    return this.getReviewReadService().getOfflineReviews();
  }

  /**
   * Update offline review
   */
  async updateOfflineReview(
    reviewId: number,
    updates: Partial<HomeReview>
  ): Promise<HomeReview> {
    await this.initialize();
    return this.getReviewMutationService().updateOfflineReview(reviewId, updates);
  }

  async queueReviewSubmission(
    reviewId: number,
    vendorNotes?: string
  ): Promise<HomeReview> {
    await this.initialize();

    return this.queueReviewLifecycleAction(reviewId, {
      type: 'submit',
      vendorNotes,
      requestedAt: new Date().toISOString(),
    });
  }

  async queueReviewCompletion(reviewId: number): Promise<HomeReview> {
    await this.initialize();

    return this.queueReviewLifecycleAction(reviewId, {
      type: 'complete',
      requestedAt: new Date().toISOString(),
    });
  }

  private async queueReviewLifecycleAction(
    reviewId: number,
    action: PendingReviewLifecycleAction
  ): Promise<HomeReview> {
    let existingReview = await offlineDatabase.getReview(reviewId);
    if (!existingReview && this.isOnline) {
      try {
        await this.cacheOnlineReview(
          await reviewCoreApi.getReviewDetails(reviewId, {
            includeTemplate: true,
            includePhotos: true,
            presignPhotos: false,
          })
        );
        existingReview = await offlineDatabase.getReview(reviewId);
      } catch (error) {
        console.warn('[OfflineReviewService] Failed to cache review before lifecycle action:', error);
      }
    }

    if (!existingReview) {
      throw new Error('Review details are not available on this device yet. Open the review while online and try again.');
    }

    const existingMetadata = this.asLifecycleMetadata(existingReview?.metadata);
    const existingAction = existingMetadata.pendingLifecycleAction;
    const pendingLifecycleAction =
      existingAction?.type === 'complete' && action.type === 'submit'
        ? existingAction
        : action.type === 'complete' && existingAction?.type === 'submit'
          ? {
              ...action,
              vendorNotes: existingAction.vendorNotes,
            }
          : action;

    return this.updateOfflineReview(reviewId, {
      status: pendingLifecycleAction.type === 'complete' ? 'completed' : 'submitted',
      submittedAt: pendingLifecycleAction.type === 'submit'
        ? new Date(pendingLifecycleAction.requestedAt)
        : existingReview?.submittedAt,
      completedAt: pendingLifecycleAction.type === 'complete'
        ? new Date(pendingLifecycleAction.requestedAt)
        : existingReview?.completedAt,
      vendorNotes: pendingLifecycleAction.vendorNotes ?? existingReview?.vendorNotes,
      metadata: {
        ...existingMetadata,
        pendingLifecycleAction,
      },
    });
  }

  /**
   * Cache an online-fetched review to offline storage
   * This ensures reviews created/fetched online are available offline
   */
  async cacheOnlineReview(
    apiReview: HomeReview,
    options: CacheOnlineReviewOptions = {}
  ): Promise<void> {
    await this.initialize();
    await this.getReviewCacheService().cacheOnlineReview(apiReview, {
      ...options,
      skipCache: Platform.OS === 'web' || options.skipCache,
    });
  }

  // ====================
  // RESPONSE MANAGEMENT
  // ====================

  /**
   * Update response with auto-save functionality
   * @param syncStatus - Optional sync status override (defaults to 'pending' for offline updates, use 'synced' for server responses)
   */
  async updateOfflineResponse(
    reviewId: number,
    itemId: number,
    data: Partial<ReviewResponse>,
    enableAutoSave: boolean = true,
    syncStatus: 'pending' | 'synced' | 'failed' = 'pending',
    mutationTimestamp?: Date
  ): Promise<ReviewResponse> {
    await this.initialize();
    return this.getResponseMutationService().updateOfflineResponse(
      reviewId,
      itemId,
      data,
      enableAutoSave,
      syncStatus,
      mutationTimestamp
    );
  }

  // ====================
  // DRAFT AUTO-SAVE
  // ====================

  /**
   * Force save all dirty drafts
   */
  async saveAllDrafts(): Promise<void> {
    await this.draftManager.saveAllDrafts();
  }

  // ====================
  // TEMPORARY ID MANAGEMENT
  // ====================

  /**
   * Generate a temporary ID
   */
  generateTempId(): string {
    return this.tempIdStore.generateTempId();
  }

  /**
   * Map temporary ID to real ID after sync
   */
  async mapTempIdToRealId(tempId: string, realId: number, entityType: 'review' | 'response' | 'room' | 'item' = 'review'): Promise<void> {
    await this.tempIdStore.mapTempIdToRealId(tempId, realId, entityType);
  }

  /**
   * Get real ID from temporary ID
   */
  async getRealId(tempId: string): Promise<number | undefined> {
    return this.tempIdStore.getRealId(tempId);
  }

  // ====================
  // SYNC MANAGEMENT
  // ====================

  private enqueueReviewSync(reviewId: number): void {
    this.syncTriggerQueue.enqueue(reviewId);
  }

  /**
   * Sync all pending and failed reviews (with retry)
   */
  async syncPendingReviews(): Promise<void> {
    this.failedReviewRecordSyncIds.clear();
    await this.getReviewRecordSyncService().syncPendingReviews(this.isOnline);

    if (!this.isOnline) {
      return;
    }

    const failedReviews = await offlineDatabase.getReviews({ syncStatus: 'failed' });
    for (const review of failedReviews) {
      this.failedReviewRecordSyncIds.add(review.id);
    }
  }

  /**
   * Sync a single review
   */
  async syncSingleReview(offlineReview: OfflineReview): Promise<void> {
    await this.getReviewRecordSyncService().syncSingleReview(offlineReview);
  }

  /**
   * Sync a single response
   */
  private async syncSingleResponse(
    realReviewId: number,
    offlineResponse: OfflineResponse,
    forcedItemId?: number
  ): Promise<boolean> {
    return this.getResponseSyncService().syncSingleResponse(
      realReviewId,
      offlineResponse,
      forcedItemId
    );
  }

  /**
   * Get sync statistics for pending/failed items
   * Used to determine if sync is needed before submission or on app startup
   */
  async getSyncStats(): Promise<OfflineSyncStats> {
    await this.initialize();
    await this.adoptValidatedLegacyRecoverableWork();

    return this.getSyncStatusService().getSyncStats();
  }

  /**
   * Check if service is initialized
   */
  isServiceInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Check if currently online
   */
  isServiceOnline(): boolean {
    return this.isOnline;
  }

  /**
   * Reconcile client state with server state
   * Detects mismatches and initiates sync if client has newer/pending data
   *
   * This is a safety net that catches any sync failures and ensures
   * client data eventually reaches the server.
   */
  async reconcileWithServer(serverReview: HomeReview): Promise<{
    needsSync: boolean;
    discrepancies: OfflineSyncDiscrepancy[];
  }> {
    await this.initialize();

    return this.getSyncReconciliationService().reconcileWithServer(serverReview, this.isOnline);
  }

  /**
   * Create offline custom room
   */
  async createOfflineRoom(propertyId: number, name: string, displayOrder?: number): Promise<string> {
    await this.initialize();

    return this.getRoomSyncService().createOfflineRoom(propertyId, name, displayOrder || 0);
  }

  /**
   * Create offline on-demand item
   */
  async createOfflineOnDemandItem(
    reviewId: number,
    data: {
      name: string;
      description?: string;
      categoryId?: number;
      questionsSchema?: any;
    }
  ): Promise<number> {
    await this.initialize();
    return this.getOnDemandItemMutationService().createOfflineOnDemandItem(reviewId, data);
  }

  /**
   * Update offline on-demand item
   */
  async updateOfflineOnDemandItem(
    reviewId: number,
    itemId: number,
    data: {
      name?: string;
      description?: string;
      categoryId?: number;
      questionsSchema?: any;
    }
  ): Promise<ReviewItem> {
    await this.initialize();
    return this.getOnDemandItemMutationService().updateOfflineOnDemandItem(
      reviewId,
      itemId,
      data
    );
  }

  /**
   * Sync offline rooms
   */
  private async syncOfflineRooms(): Promise<void> {
    await this.getRoomSyncService().syncPendingRooms();
  }

  private getOnDemandItemMutationService(): OfflineOnDemandItemMutationService {
    if (!this.onDemandItemMutationService) {
      this.onDemandItemMutationService = new OfflineOnDemandItemMutationService(
        offlineDatabase,
        reviewId => this.enqueueReviewSync(reviewId)
      );
    }

    return this.onDemandItemMutationService;
  }

  private getReviewCacheService(): OfflineReviewCacheService {
    if (!this.reviewCacheService) {
      this.reviewCacheService = new OfflineReviewCacheService(offlineDatabase);
    }

    return this.reviewCacheService;
  }

  private getReviewCreationService(): OfflineReviewCreationService {
    if (!this.reviewCreationService) {
      this.reviewCreationService = new OfflineReviewCreationService({
        addressCache: this.addressCacheService,
        callbacks: {
          registerPendingReviewTempId: tempId =>
            this.tempIdStore.registerPendingMapping(tempId, 'review'),
          enqueueReviewCreate: reviewId =>
            this.enqueueReviewSync(reviewId),
        },
        database: offlineDatabase,
        getOfflineReview: reviewId => this.getOfflineReview(reviewId),
        prefillService: this.sharedPrefillService,
        templateCache: cacheService,
      });
    }

    return this.reviewCreationService;
  }

  private getReviewMutationService(): OfflineReviewMutationService {
    if (!this.reviewMutationService) {
      this.reviewMutationService = new OfflineReviewMutationService(offlineDatabase, {
        enqueueReviewUpdate: reviewId =>
          this.enqueueReviewSync(reviewId),
        getOfflineReview: reviewId => this.getOfflineReview(reviewId),
      });
    }

    return this.reviewMutationService;
  }

  private getReviewReadService(): OfflineReviewReadService {
    if (!this.reviewReadService) {
      this.reviewReadService = new OfflineReviewReadService({
        database: offlineDatabase,
      });
    }

    return this.reviewReadService;
  }

  private getRoomSyncService(): OfflineRoomSyncService {
    if (!this.roomSyncService) {
      this.roomSyncService = new OfflineRoomSyncService(
        offlineDatabase,
        {
          createRoom: (propertyId, name) => this.createOnlineRoom(propertyId, name),
        },
        (tempId, realId) => this.mapTempIdToRealId(tempId, realId, 'room')
      );
    }

    return this.roomSyncService;
  }

  private getResponseMutationService(): OfflineResponseMutationService {
    if (!this.responseMutationService) {
      this.responseMutationService = new OfflineResponseMutationService(offlineDatabase, {
        trackDraftUpdate: (draftKey, reviewId, itemId, data) =>
          this.draftManager.trackDraftUpdate(draftKey, reviewId, itemId, data),
        registerPendingResponseTempId: tempId =>
          this.tempIdStore.registerPendingMapping(tempId, 'response'),
        enqueueResponseSync: reviewId =>
          this.enqueueReviewSync(reviewId),
      });
    }

    return this.responseMutationService;
  }

  private getResponseSyncService(): OfflineResponseSyncService {
    if (!this.responseSyncService) {
      this.responseSyncService = new OfflineResponseSyncService({
        database: offlineDatabase,
        reviewApi: {
          updateResponse: (reviewId, itemId, data) =>
            reviewCoreApi.updateResponse(reviewId, itemId, data),
          getReviewDetails: reviewId => {
            const { api } = require('./api') as typeof import('./api');
            return api.get<HomeReview>(`/reviews/${reviewId}`);
          },
        },
        getRealId: tempId => this.getRealId(tempId),
        mapTempIdToRealId: (tempId, realId) =>
          this.mapTempIdToRealId(tempId, realId, 'response'),
        processPhotoQueue: () => {
          const { photoQueueService } = require('./photoQueueService');
          return photoQueueService.processUploadQueue();
        },
      });
    }

    return this.responseSyncService;
  }

  private getReviewRecordSyncService(): OfflineReviewRecordSyncService {
    if (!this.reviewRecordSyncService) {
      this.reviewRecordSyncService = new OfflineReviewRecordSyncService({
        database: offlineDatabase,
        reviewApi: {
          createReview: (propertyId, templateId, address, detailsOrOptions) => {
            const details = detailsOrOptions && !('forceOnline' in detailsOrOptions)
              ? detailsOrOptions as ReviewContactDetails
              : undefined;
            return reviewCoreApi.createReview(propertyId, templateId, address, details);
          },
          updateReviewNotes: (reviewId, notes) =>
            reviewCoreApi.updateReviewNotes(reviewId, notes),
          updateReviewOnDemandProjects: (reviewId, onDemandProjects) =>
            reviewCoreApi.updateReviewOnDemandProjects(reviewId, onDemandProjects),
          updateReviewDetails: (reviewId, details) =>
            reviewCoreApi.updateReviewDetails(reviewId, details),
        },
        fetchReview: reviewId => {
          const { api } = require('./api') as typeof import('./api');
          return api.get<HomeReview>(`/reviews/${reviewId}`);
        },
        getRealId: tempId => this.getRealId(tempId),
        mapTempIdToRealId: (tempId, realId) =>
          this.mapTempIdToRealId(tempId, realId, 'review'),
        saveAllDrafts: () => this.saveAllDrafts(),
      });
    }

    return this.reviewRecordSyncService;
  }

  private async createOnlineRoom(propertyId: number, name: string): Promise<Room> {
    try {
      return await reviewCoreApi.createRoom(propertyId, name);
    } catch (error: any) {
      if (error?.response?.status !== 409) {
        throw error;
      }

      const existingRooms = await reviewCoreApi.getRooms(propertyId);
      const existingRoom = existingRooms.find(room =>
        room.name.trim().toLowerCase() === name.trim().toLowerCase()
      );

      if (!existingRoom) {
        throw error;
      }

      return existingRoom;
    }
  }

  private getSyncReconciliationService(): OfflineSyncReconciliationService {
    if (!this.syncReconciliationService) {
      this.syncReconciliationService = new OfflineSyncReconciliationService(
        offlineDatabase,
        () => {
          if (isDetoxAutoSyncPaused()) {
            return Promise.resolve();
          }

          return this.performFullSync();
        }
      );
    }

    return this.syncReconciliationService;
  }

  private getSyncStatusService(): OfflineSyncStatusService {
    if (!this.syncStatusService) {
      this.syncStatusService = new OfflineSyncStatusService(offlineDatabase);
    }

    return this.syncStatusService;
  }

  /**
   * Sync offline on-demand items
   */
  private async syncOfflineOnDemandItems(): Promise<void> {
    await this.getOnDemandItemSyncService().syncPendingOnDemandItems();
  }

  private getOnDemandItemSyncService(): OfflineOnDemandItemSyncService {
    if (!this.onDemandItemSyncService) {
      this.onDemandItemSyncService = new OfflineOnDemandItemSyncService({
        database: offlineDatabase,
        reviewApi: {
          getOnDemandItems: reviewId => reviewCoreApi.getOnDemandItems(reviewId),
          createOnDemandItem: (reviewId, data) =>
            reviewCoreApi.createOnDemandItem(reviewId, data),
          updateOnDemandItem: (reviewId, itemId, data) =>
            reviewCoreApi.updateOnDemandItem(reviewId, itemId, data),
        },
        getRealId: tempId => this.getRealId(tempId),
        mapTempIdToRealId: (tempId, realId, entityType) =>
          this.mapTempIdToRealId(tempId, realId, entityType),
        syncSingleResponse: (serverReviewId, response, resolvedItemId) =>
          this.syncSingleResponse(serverReviewId, response, resolvedItemId),
        recomputeReviewSyncStatus: reviewId =>
          this.getSyncStatusService().recomputeReviewSyncStatus(reviewId),
      });
    }

    return this.onDemandItemSyncService;
  }

  /**
   * Sync pending responses separately (after rooms have been synced)
   */
  private async syncPendingResponses(): Promise<void> {
    await this.getResponseSyncService().syncPendingResponses();
  }

  private async syncPendingReviewLifecycleActions(): Promise<void> {
    if (!this.isOnline) {
      return;
    }

    const reviews = await offlineDatabase.getReviews({});
    for (const review of reviews) {
      const metadata = this.asLifecycleMetadata(review.metadata);
      const pendingLifecycleAction = metadata.pendingLifecycleAction;
      if (!pendingLifecycleAction) {
        continue;
      }

      try {
        const serverReviewId = await this.resolveServerReviewId(review);
        if (serverReviewId == null) {
          continue;
        }

        if (await this.hasUnresolvedLifecyclePrerequisites(review, serverReviewId)) {
          continue;
        }

        const submissionResult = await reviewCoreApi.submitReview(
          serverReviewId,
          pendingLifecycleAction.vendorNotes ?? review.vendorNotes
        );
        const result = pendingLifecycleAction.type === 'complete'
          && submissionResult.review?.status !== 'completed'
          ? await reviewCoreApi.completeReview(serverReviewId)
          : submissionResult;

        const nextMetadata = { ...metadata };
        delete nextMetadata.pendingLifecycleAction;

        await offlineDatabase.updateReviewIfUnchanged(
          review.id,
          review.localRevision ?? 0,
          {
            status: result.review?.status ?? (
              pendingLifecycleAction.type === 'complete' ? 'completed' : 'submitted'
            ),
            completedAt: result.review?.completedAt ?? review.completedAt,
            submittedAt: result.review?.submittedAt ?? review.submittedAt,
            vendorNotes: result.review?.vendorNotes ?? review.vendorNotes,
            metadata: Object.keys(nextMetadata).length > 0 ? nextMetadata : (null as any),
            syncStatus: 'synced',
            syncError: null,
            lastServerSeenAt: result.review?.updatedAt || new Date(),
            conflictStatus: 'none',
          }
        );
      } catch (error) {
        await offlineDatabase.updateReview(review.id, {
          syncStatus: 'failed',
          syncError: error instanceof Error ? error.message : 'Unknown lifecycle sync error',
        });
      }
    }

    this.failedReviewRecordSyncIds.clear();
  }

  private async hasUnresolvedLifecyclePrerequisites(
    review: OfflineReview,
    serverReviewId: number
  ): Promise<boolean> {
    if (
      this.failedReviewRecordSyncIds.has(review.id) ||
      review.syncStatus === 'failed' ||
      review.syncStatus === 'syncing'
    ) {
      return true;
    }

    const reviewIds = new Set([review.id, serverReviewId]);
    const responses = (
      await Promise.all(
        [...reviewIds].map(reviewId => offlineDatabase.getResponsesByReviewId(reviewId))
      )
    ).flat();
    const unresolvedSyncStatuses = new Set(['pending', 'syncing', 'failed']);

    if (responses.some(response => unresolvedSyncStatuses.has(response.syncStatus))) {
      return true;
    }

    const pendingRooms = await offlineDatabase.getPendingOfflineRooms();
    if (pendingRooms.some(room => Number(room.property_id) === Number(review.propertyId))) {
      return true;
    }

    const pendingOnDemandItems = await offlineDatabase.getPendingOfflineOnDemandItems();
    if (pendingOnDemandItems.some(item => reviewIds.has(Number(item.review_id)))) {
      return true;
    }

    const responseIds = new Set<number>();
    const responseTempIds = new Set<string>();
    for (const response of responses) {
      if (Number.isFinite(Number(response.id))) {
        responseIds.add(Number(response.id));
      }
      if (response.tempId) {
        responseTempIds.add(response.tempId);
        const mappedResponseId = await this.getRealId(response.tempId);
        if (mappedResponseId != null) {
          responseIds.add(mappedResponseId);
        }
      }
    }

    const queuedPhotos = await offlineDatabase.getPhotosFromQueue({});
    const hasPendingPhotos = queuedPhotos.some(photo => {
      if (photo.status !== 'pending' && photo.status !== 'uploading' && photo.status !== 'failed') {
        return false;
      }

      const photoReviewIds = [photo.metadata?.reviewId, photo.metadata?.localReviewId]
        .map(value => Number(value))
        .filter(Number.isFinite);

      return photoReviewIds.some(reviewId => reviewIds.has(reviewId)) ||
        responseIds.has(Number(photo.reviewResponseId)) ||
        Boolean(photo.tempId && responseTempIds.has(photo.tempId));
    });
    if (hasPendingPhotos) {
      return true;
    }

    const pendingPhotoNotes = await offlineDatabase.getPendingPhotoNotes();
    return pendingPhotoNotes.some(note =>
      note.responseId != null && responseIds.has(Number(note.responseId))
    );
  }

  private async resolveServerReviewId(review: OfflineReview): Promise<number | null> {
    if (!review.tempId) {
      return review.id;
    }

    return this.getRealId(review.tempId) ?? null;
  }

  /**
   * Perform full sync (including rooms and on-demand items)
   */
  async performFullSync(): Promise<void> {
    if (isDetoxAutoSyncPaused()) {
      return;
    }

    await this.getFullSyncService().performFullSync();
    if (this.isOnline) {
      this.syncTriggerQueue.clear();
    }
  }

  getFullSyncDebugState() {
    return this.getFullSyncService().getDebugState();
  }

  async retryRecoverySync(): Promise<PhotoRecoveryCounts> {
    await this.initialize();
    const {
      photoQueueService,
      PHOTO_FILE_MISSING_ERROR,
    } = require('./photoQueueService');
    const queuedBefore = await photoQueueService.getQueuedPhotos({});
    const trackedPhotoIds = new Set<number>(
      queuedBefore
        .filter((photo: { id?: number; status: string }) => photo.id && photo.status !== 'uploaded')
        .map((photo: { id: number }) => photo.id)
    );

    await this.performFullSync();
    const result = await photoQueueService.forceRetryFailedUploads();
    const queuedAfter = await photoQueueService.getQueuedPhotos({});
    const trackedPhotosAfter = queuedAfter.filter((photo: { id?: number }) =>
      Boolean(photo.id && trackedPhotoIds.has(photo.id))
    );

    if (trackedPhotoIds.size > 0) {
      return {
        photosUploaded: trackedPhotosAfter.filter((photo: { status: string }) =>
          photo.status === 'uploaded'
        ).length,
        photosStillFailed: trackedPhotosAfter.filter((photo: { status: string; errorMessage?: string }) =>
          photo.status !== 'uploaded' && photo.errorMessage !== PHOTO_FILE_MISSING_ERROR
        ).length,
        photosMissing: trackedPhotosAfter.filter((photo: { status: string; errorMessage?: string }) =>
          photo.status !== 'uploaded' && photo.errorMessage === PHOTO_FILE_MISSING_ERROR
        ).length,
      };
    }

    return {
      photosUploaded: result.successful.length,
      photosStillFailed: result.stillFailed,
      photosMissing: result.missing,
    };
  }

  async getRecoverableWorkItems(): Promise<RecoverableWorkItem[]> {
    await this.initialize();
    await this.adoptValidatedLegacyRecoverableWork();
    return offlineDatabase.getRecoverableWorkItems();
  }

  private async adoptValidatedLegacyRecoverableWork(): Promise<void> {
    if (!this.isOnline) {
      return;
    }

    const readLegacyItems = (offlineDatabase as any).getLegacyRecoverableWorkItems;
    const legacyItems: RecoverableWorkItem[] = typeof readLegacyItems === 'function'
      ? ((await Promise.resolve(readLegacyItems()).catch(() => [])) ?? []) as RecoverableWorkItem[]
      : [];
    const reviewIds = Array.from(new Set<number>(
      legacyItems
        .map((item: RecoverableWorkItem) => item.reviewId)
        .filter((reviewId: number | undefined): reviewId is number => Number.isFinite(reviewId))
        .filter((reviewId: number) => !this.legacyRecoveryAdoptionCheckedReviewIds.has(reviewId))
    ));

    for (const reviewId of reviewIds) {
      try {
        await reviewCoreApi.getReviewDetails(reviewId, {
          includePhotos: false,
          presignPhotos: false,
          includeTemplate: false,
        });
        if (typeof (offlineDatabase as any).adoptLegacyRecoveryEnvironmentForReview === 'function') {
          await (offlineDatabase as any).adoptLegacyRecoveryEnvironmentForReview(reviewId);
        }
      } catch (error) {
        console.warn('[OfflineReviewService] Ignoring legacy saved changes for a different environment:', {
          reviewId,
          error: error instanceof Error ? error.message : String(error),
        });
      } finally {
        this.legacyRecoveryAdoptionCheckedReviewIds.add(reviewId);
      }
    }
  }

  async recoverSelectedWorkItemIds(itemIds: string[]): Promise<SelectedWorkRecoveryResult> {
    await this.initialize();

    let recovered = 0;
    let skipped = 0;
    let failed = 0;
    let shouldProcessPhotos = false;
    const photoIds = new Set<number>();
    const reviewResponseIds = new Set<number>();
    const responseTempIds = new Set<string>();
    let photosUploaded = 0;
    let photosStillFailed = 0;
    let photosMissing = 0;

    for (const itemId of itemIds) {
      try {
        const parsed = this.parseRecoverableWorkItemId(itemId);
        if (!parsed) {
          skipped += 1;
          continue;
        }

        if (parsed.kind === 'review') {
          const review = await offlineDatabase.getReview(parsed.reviewId);
          if (!review) {
            skipped += 1;
            continue;
          }

          await this.syncSingleReview({
            ...review,
            syncStatus: 'pending',
            conflictStatus: review.conflictStatus === 'server_wins' ? 'server_wins' : 'local_wins',
          });
          recovered += 1;
          continue;
        }

        if (parsed.kind === 'response') {
          const responses = await offlineDatabase.getResponsesByReviewId(parsed.reviewId);
          const response = responses.find(candidate =>
            parsed.itemId != null
              ? candidate.itemId === parsed.itemId
              : candidate.id === parsed.entityId
          );
          if (!response) {
            skipped += 1;
            continue;
          }

          await offlineDatabase.updateResponse(response.id, {
            syncStatus: 'pending',
            conflictStatus: response.conflictStatus === 'server_wins' ? 'server_wins' : 'local_wins',
            syncError: null,
          });
          const refreshedResponse = await offlineDatabase.getResponse(response.id);
          const didRecover = refreshedResponse
            ? await this.syncSingleResponse(parsed.reviewId, refreshedResponse, parsed.itemId ?? undefined)
            : false;
          if (didRecover) {
            const syncedResponse = await offlineDatabase.getResponse(response.id).catch(() => null);
            if (syncedResponse?.conflictStatus !== 'server_wins') {
              await offlineDatabase.updateResponse(response.id, {
                syncStatus: 'synced',
                syncError: null,
                conflictStatus: 'none',
                lastServerSeenAt: syncedResponse?.lastServerSeenAt || new Date(),
              });
              await offlineDatabase.markOfflineMutationsSyncedForResponse(parsed.reviewId, parsed.itemId);
            }
            recovered += 1;
            shouldProcessPhotos = true;
            reviewResponseIds.add(response.id);
            if (response.tempId) {
              responseTempIds.add(response.tempId);
            }
          } else {
            skipped += 1;
          }
          continue;
        }

        if (parsed.kind === 'photo') {
          shouldProcessPhotos = true;
          if (parsed.entityId != null) {
            photoIds.add(parsed.entityId);
          }
        }
      } catch (error) {
        failed += 1;
        console.warn('[OfflineReviewService] Failed to recover selected work item:', {
          itemId,
          error,
        });
      }
    }

    if (shouldProcessPhotos) {
      try {
        const { photoQueueService } = require('./photoQueueService');
        const photoResult = await photoQueueService.forceRetryFailedUploads({
          photoIds: [...photoIds],
          reviewResponseIds: [...reviewResponseIds],
          responseTempIds: [...responseTempIds],
        });
        photosUploaded = photoResult.successful.length;
        photosStillFailed = photoResult.stillFailed;
        photosMissing = photoResult.missing;
      } catch (error) {
        photosStillFailed += 1;
        console.warn('[OfflineReviewService] Photo recovery retry failed:', error);
      }
    }

    return {
      recovered,
      skipped,
      failed,
      photosUploaded,
      photosStillFailed,
      photosMissing,
    };
  }

  async keepServerForSelectedWorkItemIds(itemIds: string[]): Promise<{
    keptServer: number;
    skipped: number;
    failed: number;
  }> {
    await this.initialize();

    let keptServer = 0;
    let skipped = 0;
    let failed = 0;
    const serverReviewCache = new Map<number, HomeReview | null>();

    for (const itemId of itemIds) {
      try {
        const parsed = this.parseRecoverableWorkItemId(itemId);
        if (!parsed) {
          skipped += 1;
          continue;
        }

        if (!serverReviewCache.has(parsed.reviewId)) {
          const serverReview = await reviewCoreApi.getReviewDetails(parsed.reviewId, {
            includePhotos: false,
            presignPhotos: false,
            includeTemplate: false,
          }).catch(error => {
            console.warn('[OfflineReviewService] Failed to load server review for recovery choice:', {
              reviewId: parsed.reviewId,
              error,
            });
            return null;
          });
          serverReviewCache.set(parsed.reviewId, serverReview);
        }

        const serverReview = serverReviewCache.get(parsed.reviewId);

        if (parsed.kind === 'review') {
          if (serverReview) {
            await offlineDatabase.updateReview(parsed.reviewId, {
              status: serverReview.status,
              completedAt: serverReview.completedAt,
              submittedAt: serverReview.submittedAt,
              notes: serverReview.notes,
              onDemandProjects: serverReview.onDemandProjects,
              vendorNotes: serverReview.vendorNotes,
              syncStatus: 'synced',
              syncError: null,
              lastServerSeenAt: serverReview.updatedAt || new Date(),
              conflictStatus: 'server_wins',
            });
          } else {
            await offlineDatabase.updateReview(parsed.reviewId, {
              syncStatus: 'synced',
              syncError: null,
              conflictStatus: 'server_wins',
            });
          }
          await offlineDatabase.markOfflineMutationsSyncedForReview(parsed.reviewId);
          keptServer += 1;
          continue;
        }

        if (parsed.kind === 'response') {
          const responses = await offlineDatabase.getResponsesByReviewId(parsed.reviewId);
          const response = responses.find(candidate =>
            parsed.itemId != null
              ? candidate.itemId === parsed.itemId
              : candidate.id === parsed.entityId
          );
          if (!response) {
            skipped += 1;
            continue;
          }

          const serverResponse = serverReview?.responses?.find(candidate =>
            candidate.itemId === (parsed.itemId ?? response.itemId)
          );
          await offlineDatabase.updateResponse(response.id, serverResponse ? {
            isPresent: serverResponse.isPresent,
            status: serverResponse.status,
            responseData: serverResponse.responseData,
            notes: serverResponse.notes,
            roomId: serverResponse.roomId,
            floor: serverResponse.floor,
            syncStatus: 'synced',
            syncError: null,
            lastServerSeenAt: (serverResponse as any).updatedAt || new Date(),
            conflictStatus: 'server_wins',
          } : {
            syncStatus: 'synced',
            syncError: null,
            conflictStatus: 'server_wins',
          });
          await offlineDatabase.markOfflineMutationsSyncedForResponse(parsed.reviewId, parsed.itemId);
          keptServer += 1;
          continue;
        }

        skipped += 1;
      } catch (error) {
        failed += 1;
        console.warn('[OfflineReviewService] Failed to keep server for selected work item:', {
          itemId,
          error,
        });
      }
    }

    return { keptServer, skipped, failed };
  }

  private parseRecoverableWorkItemId(id: string): {
    kind: 'review' | 'response' | 'photo';
    reviewId: number;
    itemId?: number;
    entityId?: number;
  } | null {
    const parts = id.split(':');
    if (parts[0] === 'review') {
      const reviewId = Number(parts[1]);
      return Number.isFinite(reviewId) ? { kind: 'review', reviewId } : null;
    }
    if (parts[0] === 'response') {
      const reviewId = Number(parts[1]);
      const itemOrEntity = parts[2];
      if (!Number.isFinite(reviewId)) {
        return null;
      }
      if (itemOrEntity === 'response') {
        const entityId = Number(parts[3]);
        return Number.isFinite(entityId) ? { kind: 'response', reviewId, entityId } : null;
      }
      const itemId = Number(itemOrEntity);
      return Number.isFinite(itemId) ? { kind: 'response', reviewId, itemId } : null;
    }
    if (parts[0] === 'photo') {
      const entityId = Number(parts[1]);
      return Number.isFinite(entityId) ? { kind: 'photo', reviewId: -1, entityId } : null;
    }

    return null;
  }

  async restoreLocalDraft(reviewId: number): Promise<boolean> {
    await this.initialize();
    const restored = await offlineDatabase.restoreLatestRecoverySnapshot(reviewId);
    if (restored) {
      this.enqueueReviewSync(reviewId);
      if (this.isOnline) {
        this.syncTriggerQueue.scheduleDefensiveSync();
      }
    }

    return restored;
  }

  async exportRecoveryBundle(reviewId?: number): Promise<{ path?: string; bundle: Record<string, unknown> }> {
    await this.initialize();
    const bundle = await offlineDatabase.exportRecoveryBundle(reviewId);

    if (Platform.OS === 'web') {
      return { bundle };
    }

    const RNFS = require('react-native-fs');
    const fileName = `exhale-recovery-${reviewId ?? 'all'}-${Date.now()}.json`;
    const path = `${RNFS.DocumentDirectoryPath}/${fileName}`;
    await RNFS.writeFile(path, JSON.stringify(bundle, null, 2), 'utf8');
    console.warn('[OfflineReviewService] Exported recovery bundle:', {
      reviewId: reviewId ?? null,
      path,
    });

    return { path, bundle };
  }

  private getFullSyncService(): OfflineFullSyncService {
    if (!this.fullSyncService) {
      this.fullSyncService = new OfflineFullSyncService({
        getEffectiveNetworkOnline,
        getOnlineStatus: () => this.isOnline,
        setOnlineStatus: isOnline => {
          this.isOnline = isOnline;
        },
        saveAllDrafts: () => this.saveAllDrafts(),
        syncPendingReviews: () => this.syncPendingReviews(),
        syncOfflineRooms: () => this.syncOfflineRooms(),
        syncOfflineOnDemandItems: () => this.syncOfflineOnDemandItems(),
        syncPendingResponses: () => this.syncPendingResponses(),
        processPhotoQueue: () => {
          const { photoQueueService } = require('./photoQueueService');
          return photoQueueService.processUploadQueue();
        },
        syncPendingPhotoNotes: () => {
          const { photoQueueService } = require('./photoQueueService');
          return photoQueueService.syncPendingPhotoNotes();
        },
        syncReviewLifecycleActions: () => this.syncPendingReviewLifecycleActions(),
        recomputeReviewSyncStatuses: () =>
          this.getSyncStatusService().recomputeReviewSyncStatuses(),
        logger: console,
      });
    }

    return this.fullSyncService;
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.draftManager.cleanup();
    this.networkMonitor.cleanup();
    this.stopPeriodicSync();

    // Clear temp ID mappings
    this.tempIdStore.clear();

    // Clear pending sync triggers
    this.syncTriggerQueue.clear();
    this.sharedPrefillService.clearCache();

    // Reset initialization state
    this.isInitialized = false;
    this.isOnline = false;
    this.legacyRecoveryAdoptionCheckedReviewIds.clear();
    this.failedReviewRecordSyncIds.clear();
  }
}

// Export singleton instance
export const offlineReviewService = new OfflineReviewService();
